import type { AdminUserResponse } from '@manasil/shared';
import { useState } from 'react';
import { Shell } from '../components/Shell.js';
import { Empty, Loading } from '../components/ui.js';
import { useUsers } from '../lib/queries.js';

type Filter = 'all' | 'onboarded' | 'pending';

function joined(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Who has signed up and what they chose.
 *
 * A table rather than cards: this list is unbounded and gets scanned, not browsed — unlike
 * goals and categories, which are a fixed handful.
 */
export function UsersPage() {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const onboarded = filter === 'all' ? undefined : filter === 'onboarded';
  const { data, isLoading } = useUsers({
    search,
    ...(onboarded !== undefined ? { onboarded } : {}),
  });

  return (
    <Shell
      title="App users"
      subtitle="Everyone who has verified a code, and the goals they picked during onboarding."
    >
      <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
        <input
          className="input"
          style={{ maxWidth: 260 }}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search by mobile number…"
        />
        <div className="row" style={{ gap: 6 }}>
          {(['all', 'onboarded', 'pending'] as Filter[]).map((value) => (
            <button
              key={value}
              type="button"
              className="chip"
              aria-pressed={filter === value}
              onClick={() => setFilter(value)}
            >
              {value === 'all' ? 'All' : value === 'onboarded' ? 'Onboarded' : 'Not onboarded'}
            </button>
          ))}
        </div>
        {data ? (
          <span className="hint" style={{ marginLeft: 'auto' }}>
            {data.onboarded} of {data.total} have completed onboarding
          </span>
        ) : null}
      </div>

      <div className="card">
        {isLoading ? (
          <Loading />
        ) : !data || data.items.length === 0 ? (
          <Empty
            mark="◍"
            title={search ? 'No one matches that' : 'No users yet'}
            hint={search ? undefined : 'Sign in on the app and they will appear here.'}
          />
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Mobile</th>
                  <th>Name</th>
                  <th>Goals</th>
                  <th>Daily time</th>
                  <th>Streak</th>
                  <th>Joined</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((user) => (
                  <UserRow key={user.id} user={user} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {data?.nextCursor ? (
        <p className="hint">
          Showing the {data.items.length} most recent. Paging arrives when the list needs it.
        </p>
      ) : null}

      <p className="hint" style={{ maxWidth: '76ch' }}>
        Goals are shown in the order the user chose them — the first is their{' '}
        <strong>main focus</strong>, and it is what decides which journey they are matched with.
        Deleted accounts stay listed: deletion is soft, so their history is intact.
      </p>
    </Shell>
  );
}

function UserRow({ user }: { user: AdminUserResponse }) {
  const deleted = user.deletedAt !== null;

  return (
    <tr style={deleted ? { opacity: 0.5 } : undefined}>
      <td className="mono" style={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>
        {user.mobile}
      </td>
      <td>{user.name ?? <span className="dim">—</span>}</td>
      <td>
        {user.goals.length === 0 ? (
          <span className="dim">—</span>
        ) : (
          <div className="chips">
            {user.goals.map((goal) => (
              <span
                key={goal.key}
                className={`pill ${goal.priority === 0 ? 'pill-ok' : 'pill-draft'}`}
                title={goal.priority === 0 ? 'Main focus' : undefined}
              >
                {goal.priority === 0 ? <span className="dot" /> : null}
                {goal.name}
              </span>
            ))}
          </div>
        )}
      </td>
      <td className="nums muted">
        {user.dailyMinutes ? `${user.dailyMinutes} min` : <span className="dim">—</span>}
      </td>
      <td className="nums muted">{user.streakCount}</td>
      <td className="muted" style={{ whiteSpace: 'nowrap' }}>
        {joined(user.createdAt)}
      </td>
      <td>
        <span
          className={`pill ${deleted ? 'pill-draft' : user.onboardingCompleted ? 'pill-ok' : 'pill-missing'}`}
        >
          {deleted ? 'Deleted' : user.onboardingCompleted ? 'Onboarded' : 'Pending'}
        </span>
      </td>
    </tr>
  );
}
